import ChatbotAPI from "./api.js";
import DOMPurify from "dompurify";
import { marked } from "marked";

class Chatbot {
  constructor() {
    this.api = new ChatbotAPI();
    this.chatbotContainer = null;
    this.messagesContainer = null;
    this.loadingElement = null;
    this.loadingTextElement = null;
    this.chatbotInput = null;
    this.sendButton = null;
    this.currentMessageId = null;
    this.roomId = null;
    this.sources = [];
  }

  async init() {
    // Get references to elements
    this.chatbotContainer = document.getElementById("chatbot-container");
    this.messagesContainer = document.getElementById("chatbot-messages");
    this.chatbotInput = document.getElementById("chatbot-input");
    this.sendButton = document.getElementById("chatbot-send");

    const isInitilized = this.initConversationHistory();
    this.loadingElement = document.getElementById("chatbot-loading");
    this.loadingTextElement = document.getElementById("chatbot-loading-text");

    this.roomId = this.chatbotContainer.dataset.roomId;
    this.loadingElement.hidden = true;

    this.setupEventListeners();
    if (!isInitilized) {
      this
        .addDisclaimerMessage(`TOMO accompagne dans le temps, avec des humains, et transforme le vécu en soutien utile, en combinant l’intelligence artificielle et l’intervention de pairs-aidants formés.
        Les échanges proposés reposent sur le partage d’expérience et ne constituent ni un avis médical, ni un diagnostic, ni une recommandation thérapeutique.
        En cas d’urgence ou de danger immédiat, contactez sans attendre les services d’urgence ou un professionnel de santé.`);
    }
  }

  setupEventListeners() {
    this.sendButton.addEventListener("click", () => {
      this.handleSendMessage();
      this.textareaAutoResize();
    });

    this.chatbotInput.addEventListener("keypress", (e) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        this.handleSendMessage();
        this.textareaAutoResize();
      }
    });

    this.closeButton = document.getElementById("chatbot-close");
    if (this.closeButton) {
      this.closeButton.addEventListener("click", () => this.minimizeChatbot());
    }

    this.chatbotInput.addEventListener("input", () =>
      this.textareaAutoResize(),
    );
  }

  async handleSendMessage() {
    this.maximizeChatbot();
    const message = this.chatbotInput.value.trim();
    if (message) {
      this.loadingElement.hidden = false;
      this.addUserMessage(message);
      this.chatbotInput.value = "";
      await this.api.sendMessageStream(message, this.roomId, {
        onStart: () => {},
        onFirstChunk: () => {
          this.loadingElement.hidden = true;
          this.resetLoadingText();
        },
        onAssistantMessage: (assistantMessage) => {
          if (assistantMessage.message_id !== this.currentMessageId) {
            this.currentMessageId = assistantMessage.message_id;
          }

          this.updateAssistantMessage(assistantMessage);
        },
        onPeerSupportMessage: (peerSupport) => {
          this.addPeerSupportMessage(peerSupport);
        },
        onSourceMessage: (source) => {
          this.addSource(source);
        },
        onToolMessage: (toolMessage) => {
          this.updateLoadingText(toolMessage);
        },
        onDone: (data) => {
          if (data.conversation_id) {
            this.setConversationId(data.conversation_id);
          }

          if (this.sources.length !== 0) {
            this.transformSource();
            this.addSourceMessage();
          }

          this.saveConversationHistory();
        },
        onError: (error) => {
          this.loadingElement.hidden = true;
          this.addErrorMessage(error);
          this.resetLoadingText();
        },
      });
    }
  }

  addUserMessage(message) {
    const messageElement = document.createElement("div");
    messageElement.className = "chatbot-message chatbot-user-message";
    messageElement.textContent = message;
    this.messagesContainer.insertBefore(messageElement, this.loadingElement);
    this.scrollToBottom();
  }

  addAssistantMessage(assistantMessage) {
    const messageElement = document.createElement("div");
    messageElement.id = assistantMessage.message_id;
    messageElement.className =
      "chatbot-message chatbot-assistant-message markdown-content";

    this.messagesContainer.insertBefore(messageElement, this.loadingElement);
    this.scrollToBottom();

    return messageElement;
  }

  updateAssistantMessage(assistantMessage) {
    let messageElement = document.getElementById(assistantMessage.message_id);
    if (!messageElement) {
      messageElement = this.addAssistantMessage(assistantMessage);
    }

    // Récupérer le texte brut existant ou initialiser
    const existingText = messageElement.dataset.rawText || "";
    const newText = existingText + assistantMessage.content;

    // Stocker le texte brut dans une propriété data
    messageElement.dataset.rawText = newText;

    // Générer et injecter le HTML
    const dirtyHtml = marked(newText);
    const cleanHtml = DOMPurify.sanitize(dirtyHtml);

    messageElement.innerHTML = cleanHtml;

    this.scrollToBottom();

    return messageElement;
  }

  addSourceMessage() {
    const sourcesElement = document.createElement("div");
    sourcesElement.className = "chatbot-message chatbot-sources-message";
    const sourcesTitle = document.createElement("div");
    sourcesTitle.className = "chatbot-sources-message__title";
    sourcesTitle.innerHTML = `<i class="fas fa-link"></i> Sources`;
    sourcesElement.appendChild(sourcesTitle);
    const sourcesContent = document.createElement("div");
    sourcesContent.className = "chatbot-sources-message__content";
    sourcesElement.appendChild(sourcesContent);

    this.sources.forEach((source, index) => {
      if (
        source &&
        source.content &&
        source.content.url &&
        source.content.title
      ) {
        const sourceLink = document.createElement("a");
        sourceLink.className = "chatbot-sources-message__link";

        const host = this.getHost(source.content.url);
        sourceLink.href = source.content.url;
        sourceLink.target = "_blank";
        sourceLink.rel = "noopener noreferrer";
        sourceLink.textContent = `[${index + 1}] ${host} - ${source.content.title}`;

        sourcesContent.appendChild(sourceLink);
      }
    });

    this.setupSourcesEventListener(sourcesTitle, sourcesContent);
    this.sources = [];
    this.messagesContainer.insertBefore(sourcesElement, this.loadingElement);
    this.scrollToBottom();

    return sourcesElement;
  }

  addSource(source) {
    const existingSourceIndex = this.sources.findIndex(
      (s) => s.content.url === source.content.url,
    );

    let sourceIndex;
    if (existingSourceIndex !== -1) {
      sourceIndex = existingSourceIndex + 1;
    } else {
      sourceIndex = this.sources.push(source);
    }

    this.updateAssistantMessage({
      message_id: source.message_id,
      content: ` [${sourceIndex}]`,
    });
  }

  transformSource() {
    let messageElement = document.getElementById(this.currentMessageId);
    const htmlText = messageElement.innerHTML;
    messageElement.innerHTML = htmlText.replace(
      /\[(\d+)\]/g,
      (match, indexStr) => {
        const index = parseInt(indexStr, 10) - 1;
        const source = this.sources[index];
        if (source) {
          return ` <a href="${source.content.url}" title="${source.content.title}" target="_blank" rel="noopener noreferrer">[${indexStr}]</a>`;
        }

        return match;
      },
    );
  }

  updateLoadingText(toolMessage) {
    this.loadingTextElement.textContent = toolMessage.content;
  }

  resetLoadingText() {
    this.loadingTextElement.textContent = "Analyse";
  }

  addPeerSupportMessage(peerSupport) {
    const peerSupportHtml = `
      <div class="chatbot-peer-support-message">
        <div><strong>${peerSupport.name}</strong></div>
        <div>Spécialité: ${peerSupport.pathologie || "Non spécifiée"}</div>
        <a href="${peerSupport.url}" target="_blank" rel="noopener noreferrer" class="lp-btn lp-btn-primary">Prendre rendez-vous</a>
      </div>
    `;
    const messageElement = document.createElement("div");
    this.messagesContainer.insertBefore(messageElement, this.loadingElement);
    messageElement.outerHTML = peerSupportHtml;
    this.scrollToBottom();
  }

  addErrorMessage(errorMessage) {
    const messageElement = document.createElement("div");
    messageElement.className = `chatbot-message chatbot-error-message`;
    messageElement.innerHTML = `<i class="fas fa-exclamation-triangle"></i> ${errorMessage}`;

    this.messagesContainer.insertBefore(messageElement, this.loadingElement);
    this.scrollToBottom();

    return messageElement;
  }

  addDisclaimerMessage(disclaimerMessage) {
    const messageElement = document.createElement("div");
    messageElement.className = `chatbot-message markdown-content chatbot-disclaimer-message`;
    messageElement.innerHTML = `<i class="fas fa-info-circle"></i> ${disclaimerMessage}`;

    this.messagesContainer.insertBefore(messageElement, this.loadingElement);
    this.scrollToBottom();

    return messageElement;
  }

  addMarkdownMessage(message, className) {
    const messageElement = document.createElement("div");
    messageElement.className = `chatbot-message markdown-content ${className}`;
    // Stocker le texte brut dans une propriété data
    messageElement.dataset.rawText = message.content;

    // Générer et injecter le HTML
    const dirtyHtml = marked(message.content);
    const cleanHtml = DOMPurify.sanitize(dirtyHtml);
    messageElement.innerHTML = cleanHtml;
    this.messagesContainer.insertBefore(messageElement, this.loadingElement);
    this.scrollToBottom();

    return messageElement;
  }

  minimizeChatbot() {
    this.chatbotContainer.classList.remove("maximized");
  }

  maximizeChatbot() {
    this.chatbotContainer.classList.add("maximized");
  }

  scrollToBottom() {
    this.messagesContainer.scrollTop = this.messagesContainer.scrollHeight;
  }

  textareaAutoResize() {
    this.chatbotInput.style.height = "auto";
    const newHeight = Math.min(this.chatbotInput.scrollHeight, 120);
    this.chatbotInput.style.height = newHeight + "px";
    this.chatbotInput.scrollTop = this.chatbotInput.scrollHeight;
  }

  getHost(url) {
    const host = new URL(url).host;

    return host.replace(/^www\./, "");
  }

  setupSourcesEventListener(sourcesTitle, sourcesContent) {
    sourcesTitle.addEventListener("click", () => {
      sourcesTitle.classList.toggle("chatbot-sources-message__title--open");
      sourcesContent.classList.toggle("chatbot-sources-message__content--open");
    });
  }

  setConversationId(conversationId) {
    const maxAge = 2 * 60 * 60; // 2h en secondes
    document.cookie = [
      `conversation_id=${encodeURIComponent(conversationId)}`,
      `Max-Age=${maxAge}`,
      `Path=/`,
      `SameSite=Lax`,
      `Secure`,
    ].join("; ");
  }

  saveConversationHistory() {
    try {
      sessionStorage.setItem(
        "chatbot_conversation",
        this.messagesContainer.innerHTML,
      );
    } catch (e) {
      if (e.name === "QuotaExceededError") {
        console.warn("sessionStorage plein");
      }
    }
  }

  initConversationHistory() {
    const savedConversation = sessionStorage.getItem("chatbot_conversation");
    if (savedConversation) {
      this.messagesContainer.innerHTML = savedConversation;
      this.scrollToBottom();
    }

    return savedConversation ? true : false;
  }
}

export default Chatbot;
